import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, EmptyState, ErrorState, PageHeader, Pill, SegmentedControl, Skeleton } from "../components/ui";
import { createAiInsights, fetchAiInsights, fetchPantryItems } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { assetUrl } from "../lib/assets";
import { useShellSession } from "../hooks/useShellSession";
import type { AIInsightRequest, AIInsightRequestListResponse, PantryItem } from "../types";

type FilterValue = "all" | "complete" | "failed";

function normalizeList(payload: AIInsightRequest[] | AIInsightRequestListResponse): AIInsightRequest[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  return payload.items ?? [];
}

function formatDate(value?: string): string {
  if (!value) {
    return "Unknown";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function filterByStatus(items: AIInsightRequest[], filter: FilterValue): AIInsightRequest[] {
  if (filter === "all") {
    return items;
  }
  if (filter === "complete") {
    return items.filter((item) => item.status.toLowerCase() === "complete");
  }
  return items.filter((item) => {
    const status = item.status.toLowerCase();
    return status === "failed" || status === "expired";
  });
}

function toPantryPayload(items: PantryItem[]): Array<Record<string, unknown>> {
  return items.map((item) => ({
    name: item.item_name,
    category: item.category ?? null,
    brand: item.brand ?? null,
    notes: item.notes ?? null,
    normalized_name: item.normalized_name || null,
    quantity: item.quantity === null || item.quantity === undefined ? null : String(item.quantity),
  }));
}

export function AIInsightsPage() {
  const { session } = useShellSession();
  const isDietitian = Boolean(session.user?.isDietitian);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [requests, setRequests] = useState<AIInsightRequest[]>([]);
  const [filter, setFilter] = useState<FilterValue>("complete");

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchAiInsights();
      setRequests(normalizeList(payload));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load AI insight jobs.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function createAnalysis(): Promise<void> {
    if (creating) {
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const pantryResponse = await fetchPantryItems(200);
      const pantryItems = pantryResponse.items ?? [];
      if (pantryItems.length === 0) {
        setError("Add pantry items before running analysis.");
        trackEvent("ai_insight_create_blocked", { reason: "empty_pantry" });
        return;
      }

      const created = await createAiInsights({ pantry_items: toPantryPayload(pantryItems) });
      setRequests((current) => [created, ...current.filter((item) => item.id !== created.id)]);
      setFilter("all");
      trackEvent("ai_insight_requested", { pantry_item_count: pantryItems.length });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start AI analysis.");
    } finally {
      setCreating(false);
    }
  }

  const visible = useMemo(() => filterByStatus(requests, filter), [requests, filter]);

  return (
    <div data-testid="ai-insights-page" className="space-y-4 pb-4">
      <PageHeader
        title="AI Insights"
        subtitle="Pantry summary, nutrition patterns, and practical next steps."
        right={
          isDietitian ? (
            <Link to="/app/ai-insights/review" className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold">
              Dietitian review
            </Link>
          ) : null
        }
      />

      <Card className="overflow-hidden p-0">
        <div className="relative h-52 sm:h-60">
          <img src={assetUrl("ai-insights-hero.png")} alt="AI Insights" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/20 to-black/10" />
          <div className="absolute inset-x-0 bottom-0 p-5 text-white">
            <h2 className="pr-display text-2xl font-semibold sm:text-3xl">Analyze your pantry now</h2>
            <p className="mt-1 text-sm text-white/85">Generate AI-powered suggestions using your latest pantry data.</p>
            <Button className="mt-3" onClick={() => void createAnalysis()} disabled={creating}>
              {creating ? "Starting analysis..." : "Run analysis"}
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <SegmentedControl
          value={filter}
          options={[
            { value: "all", label: "All" },
            { value: "complete", label: "Complete" },
            { value: "failed", label: "Failed" },
          ]}
          onChange={setFilter}
        />
      </Card>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => void load()} />
      ) : visible.length === 0 ? (
        <EmptyState
          title="No analysis history"
          subtitle="Run your first pantry analysis to get personalized suggestions."
          image={assetUrl("ai-insights-hero.png")}
          action={
            <Button onClick={() => void createAnalysis()} disabled={creating}>
              {creating ? "Starting..." : "Analyze pantry"}
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {visible.map((request) => (
            <Card key={request.id} flat>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold">Pantry analysis</h3>
                  <p className="mt-1 text-sm text-[var(--pr-text-muted)]">Created {formatDate(request.created_at)}</p>
                  {request.summary ? <p className="mt-2 text-sm text-[var(--pr-text-muted)]">{request.summary}</p> : null}
                  {request.error_message ? <p className="mt-2 text-sm text-rose-600">{request.error_message}</p> : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="accent" label={request.status.toUpperCase()} />
                  <Link to={`/app/ai-insights/${request.id}`} className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold">
                    Open
                  </Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
